import { gql } from "lib/apolloClient";

/**
 * Phase 1 query: Fetches ONLY IDs for courses, grades, and curriculum programs.
 * No names, no counts, no avatars — minimal payload for 14k+ courses.
 */
export const getOrganizationCourseIdsHierarchyQuery = gql`
  query getOrganizationCourseIdsHierarchy(
    $organizationId: ID!
    $courseFilters: CourseFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        courses(filters: $courseFilters) {
          edges {
            node {
              id
              grades {
                id
              }
              curriculumProgram {
                id
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * Non-admin Phase 1 query: Fetches ONLY IDs for courses, grades, and curriculum programs
 * for a specific staff user via coursesV2. Matches the edge shape expected by buildHierarchyFromEdges.
 */
export const getUserCoursesV2HierarchyQuery = gql`
  query getUserCoursesV2Hierarchy(
    $id: ID!
    $filters: CourseFilter
    $userType: ENTITY_TYPE_ENUM!
  ) {
    node(id: $id, type: $userType) {
      id
      ... on Staff {
        id
        coursesV2(orderBy: TITLE, orderByDirection: ASC, filters: $filters) {
          edges {
            node {
              id
              grades {
                id
              }
              curriculumProgram {
                id
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * Phase 2 query: Fetches course title and grade names for visible course IDs.
 * Uses multiNode to batch-fetch by IDs.
 */
export const getCourseDetailsByIdsQuery = gql`
  query getCourseDetailsByIds($courseIds: [ID!]) {
    multiNode(ids: $courseIds, type: COURSE) {
      id
      ... on Course {
        title
        grades {
          id
          name
          displaySequence
        }
      }
    }
  }
`;

/**
 * Fetches user counts (students, collaborators, family) for visible course IDs.
 * Duplicated from EventModalV2Query to avoid coupling to MyCalendar route.
 */
export const getOrganizationCoursesUserCountQuery = gql`
  query getOrganizationCoursesUserCount($courseIds: [ID!]) {
    multiNode(ids: $courseIds, type: COURSE) {
      id
      ... on Course {
        students {
          totalCount
        }
        collaborator {
          totalCount
        }
        familyMembers {
          totalCount
        }
      }
    }
  }
`;

/**
 * Fetches aggregated user counts for a single school (curriculum program).
 * Used to build per-school count subtitles in SchoolHeader.
 */
export const getSchoolUserCountQuery = gql`
  query getSchoolUserCount(
    $organizationId: ID!
    $input: UserCountByEntityInput!
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        users {
          userCountByEntity(input: $input) {
            totalCount
            staffCount
            studentCount
            familyCount
          }
        }
      }
    }
  }
`;

/**
 * Fetches aggregated user counts by entity type (school/grade/course level).
 * Used for school-level and all-schools aggregate counts.
 * Duplicated from EventModalV2Query to avoid coupling to MyCalendar route.
 */
export const getUserCountByEntityandEntityIdsQuery = gql`
  query userCountByEntityCourseWithEntityIds(
    $organizationId: ID!
    $input: UserCountByEntityInput!
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        users {
          userCountByEntity(input: $input) {
            totalCount
            staffCount
            studentCount
            familyCount
          }
        }
      }
    }
  }
`;
